import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { useLenis } from "lenis/react";
import { X } from "lucide-react";
import { useContent } from "./ContentContext";

const SHOWN_KEY = "bt_popups_shown";

export const PAGE_OPTIONS = [
  { id: "home", label: "Homepage" },
  { id: "menu", label: "Full Menu page" },
  { id: "drinks", label: "Bar Menu page" },
];

export const SECTION_OPTIONS = {
  home: ["hero", "story", "signatures", "menu", "happy-hour", "celebrate", "gallery", "visit"],
  menu: [],
  drinks: ["happy-hour"],
};

export const ANIMATION_OPTIONS = ["zoom", "slide-up", "flip", "bounce", "blur-in", "pop"];

const VARIANTS = {
  zoom: { initial: { opacity: 0, scale: 0.6 }, animate: { opacity: 1, scale: 1 }, exit: { opacity: 0, scale: 0.6 }, transition: { duration: 0.4, ease: [0.16, 1, 0.3, 1] } },
  "slide-up": { initial: { opacity: 0, y: 140 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: 140 }, transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] } },
  flip: { initial: { opacity: 0, rotateY: 90 }, animate: { opacity: 1, rotateY: 0 }, exit: { opacity: 0, rotateY: -90 }, transition: { duration: 0.55, ease: [0.16, 1, 0.3, 1] } },
  bounce: { initial: { opacity: 0, scale: 0.3 }, animate: { opacity: 1, scale: 1 }, exit: { opacity: 0, scale: 0.3 }, transition: { type: "spring", stiffness: 320, damping: 14 } },
  "blur-in": { initial: { opacity: 0, filter: "blur(22px)", scale: 1.1 }, animate: { opacity: 1, filter: "blur(0px)", scale: 1 }, exit: { opacity: 0, filter: "blur(22px)", scale: 1.05 }, transition: { duration: 0.5, ease: "easeOut" } },
  pop: { initial: { opacity: 0, scale: 0.2, rotate: -8 }, animate: { opacity: 1, scale: 1, rotate: 0 }, exit: { opacity: 0, scale: 0.4, rotate: 8 }, transition: { type: "spring", stiffness: 260, damping: 15 } },
};

// Is the popup within its schedule window right now? No schedule => always active.
export const scheduleActive = (schedule, now = new Date()) => {
  if (!schedule || !schedule.enabled) return true;
  const days = schedule.days || [];
  if (days.length && !days.includes(now.getDay())) return false;
  if (!schedule.start || !schedule.end) return true;
  const [sh, sm] = schedule.start.split(":").map(Number);
  const [eh, em] = schedule.end.split(":").map(Number);
  const cur = now.getHours() * 60 + now.getMinutes();
  const s = sh * 60 + sm;
  const e = eh * 60 + em;
  return e < s ? (cur >= s || cur <= e) : (cur >= s && cur <= e);
};

const pageKey = (pathname) => (pathname === "/menu" ? "menu" : pathname === "/drinks" ? "drinks" : pathname === "/" ? "home" : null);

const readShown = () => {
  try { return JSON.parse(sessionStorage.getItem(SHOWN_KEY) || "[]"); } catch { return []; }
};

export const PopupAds = () => {
  const { pathname } = useLocation();
  const { settings, resolveImg } = useContent();
  const navigate = useNavigate();
  const lenis = useLenis();
  const [activeId, setActiveId] = useState(null);
  const shownRef = useRef(readShown());

  const page = pageKey(pathname);
  const popups = settings.popups || [];

  useEffect(() => {
    if (!page) return undefined;
    setActiveId(null);
    const timers = [];
    const observers = [];

    const eligible = popups.filter(
      (p) => p && p.enabled && p.page === page && p.image && !shownRef.current.includes(p.id)
    );

    const tryShow = (p) => {
      if (!scheduleActive(p.schedule)) return;
      setActiveId((cur) => (cur ? cur : p.id));
    };

    eligible.forEach((p) => {
      if (p.trigger === "section" && p.sectionId) {
        const el = document.getElementById(p.sectionId);
        if (el) {
          const obs = new IntersectionObserver(
            (entries) => { entries.forEach((en) => { if (en.isIntersecting) tryShow(p); }); },
            { threshold: 0.35 }
          );
          obs.observe(el);
          observers.push(obs);
        }
      } else {
        const t = setTimeout(() => tryShow(p), Math.max(300, p.delay || 1500));
        timers.push(t);
      }
    });

    return () => {
      timers.forEach(clearTimeout);
      observers.forEach((o) => o.disconnect());
    };
  }, [page, popups]);

  if (!page) return null;
  const active = popups.find((p) => p.id === activeId);

  const close = () => {
    if (active && active.frequency !== "always") {
      shownRef.current = [...shownRef.current, active.id];
      try { sessionStorage.setItem(SHOWN_KEY, JSON.stringify(shownRef.current)); } catch { /* ignore */ }
    }
    setActiveId(null);
  };

  const onCta = () => {
    const href = active?.ctaHref;
    close();
    if (!href) return;
    if (href.startsWith("#")) {
      const el = document.querySelector(href);
      if (el) { if (lenis) lenis.scrollTo(el, { offset: -80 }); else el.scrollIntoView({ behavior: "smooth" }); }
    } else if (href.startsWith("/")) {
      navigate(href);
    } else {
      window.open(href, "_blank", "noopener,noreferrer");
    }
  };

  const v = active ? (VARIANTS[active.animation] || VARIANTS.zoom) : null;

  return (
    <AnimatePresence>
      {active && (
        <motion.div
          className="fixed inset-0 z-[120] flex items-center justify-center p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={close}
          data-testid="popup-overlay"
          style={{ backdropFilter: "blur(6px)", background: "rgba(14,8,11,0.72)" }}
        >
          <motion.div
            onClick={(e) => e.stopPropagation()}
            initial={v.initial} animate={v.animate} exit={v.exit} transition={v.transition}
            style={{ transformPerspective: 1200 }}
            className="relative w-full max-w-sm md:max-w-md bg-cantina-cream rounded-3xl overflow-hidden shadow-[0_40px_120px_rgba(0,0,0,0.6)] border border-white/10"
            data-testid="popup-card"
          >
            <button onClick={close} aria-label="Close" data-testid="popup-close"
              className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-cantina-cocoa/70 text-cantina-cream flex items-center justify-center hover:bg-cantina-cocoa transition-colors">
              <X size={18} />
            </button>

            <div className="w-full bg-cantina-cocoadeep">
              <img src={resolveImg(active.image)} alt={active.title || "Special"} className="w-full h-auto max-h-[52vh] object-contain mx-auto" data-testid="popup-image" />
            </div>

            {(active.title || active.subtitle || active.ctaLabel) && (
              <div className="p-6 text-center">
                {active.title && <h3 className="font-display font-black text-2xl md:text-3xl text-cantina-ink leading-tight" data-testid="popup-title">{active.title}</h3>}
                {active.subtitle && <p className="mt-2 font-sans text-sm md:text-base text-cantina-ink/70 leading-relaxed" data-testid="popup-subtitle">{active.subtitle}</p>}
                {active.ctaLabel && (
                  <button onClick={onCta} data-testid="popup-cta"
                    className="btn-primary mt-5 inline-block bg-cantina-pink text-cantina-cream font-sans text-sm font-bold tracking-wide px-8 py-3.5 rounded-full">
                    {active.ctaLabel}
                  </button>
                )}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
